import { useEffect, useState, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectMFControlPanelAnomaly,
  selectMFControlPanelConsultRequestId,
  selectMFControlPanelDateOffset,
  selectMFControlPanelForecastUnixDate,
  selectMFControlPanelLastConsulted,
  selectMFControlPanelModels,
  selectMFControlPanelPeriod,
  selectMFControlPanelRegion,
  selectMFControlPanelVariable,
  selectMFControlPanelViewMode,
  setHeatmapImages,
  setLastConsulted,
} from 'redux/reducers/meteorologyFieldsControlPanelReducer';
import { NumericForecastImage } from 'helper/products/meteorology';
import {
  useLazyGetNumericForecastImagesQuery,
  useLazyGetNumericForecastHeatmapQuery,
} from 'services/api/Meteorology';
import useMaxOffset from './useMaxOffset';
import useImageRowSize from '../useImageRowSize';

/**
 * Reporta falha do fluxo de imagens ao Sentry COM contexto.
 *
 * Antes, estas cadeias tinham `.finally()` sem `.catch()` — e `finally` repassa a rejeição.
 * Qualquer throw aqui virava `unhandledrejection` capturado pelo handler global, cujo stack
 * só contém frames internos de react-dom/react-redux: sem arquivo, sem linha, sem filtro.
 * Foi assim que o "Maximum update depth exceeded" de 2026-08 chegou ilegível.
 */
const reportImagesFailure = (error: unknown, context: Record<string, unknown>) => {
  Sentry.captureException(error, {
    tags: { feature: 'meteorology-numericforecast-images' },
    extra: context,
  });
};

export default function useImages(multiMapMode: boolean) {
  const dispatch = useDispatch();
  const { rowSize, setRowSize, setMaxRowSize } = useImageRowSize();
  const rowSizeRef = useRef(rowSize);
  const [images, setImages] = useState<NumericForecastImage[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [areAllImagesFetched, setAreAllImagesFetched] = useState(false);
  const forecastUnixDate = useSelector(selectMFControlPanelForecastUnixDate);
  const models = useSelector(selectMFControlPanelModels);
  const region = useSelector(selectMFControlPanelRegion);
  const basePeriod: string = useSelector(selectMFControlPanelPeriod);
  const offset: number = useSelector(selectMFControlPanelDateOffset) || 0;
  const anomaly: boolean = useSelector(selectMFControlPanelAnomaly);
  const variable: string = useSelector(selectMFControlPanelVariable);
  const viewMode = useSelector(selectMFControlPanelViewMode);
  const consultRequestId = useSelector(selectMFControlPanelConsultRequestId);
  const lastConsulted = useSelector(selectMFControlPanelLastConsulted);

  const [trigger, { error }] = useLazyGetNumericForecastImagesQuery();
  const [heatmapTrigger, { error: heatmapError }] = useLazyGetNumericForecastHeatmapQuery();

  const maxOffset = useMaxOffset();

  useEffect(() => {
    rowSizeRef.current = rowSize;
  }, [rowSize]);

  useEffect(() => {
    if (consultRequestId === 0) return undefined;
    let canceled = false;
    let maxRowSize = 0;

    dispatch(setLastConsulted({
      viewMode,
      variable,
      models,
      region,
      period: basePeriod,
      forecastUnixDate,
      anomaly,
    }));

    if (viewMode === 'heatmap') {
      if (variable && forecastUnixDate && models.length > 0) {
        setIsFetching(true);
        setAreAllImagesFetched(false);
        heatmapTrigger({ variable, models, forecastUnixDate }, true)
          .then((res) => {
            if (canceled) return;
            const heatmapImages = res.data?.data?.images ?? [];
            dispatch(setHeatmapImages(heatmapImages));
          })
          .catch((failure) => reportImagesFailure(failure, {
            fluxo: 'heatmap', variable, models, forecastUnixDate,
          }))
          .finally(() => {
            if (canceled) return;
            setIsFetching(false);
            setAreAllImagesFetched(true);
          });
      } else {
        dispatch(setHeatmapImages([]));
        setIsFetching(false);
        setAreAllImagesFetched(false);
      }
      return () => { canceled = true; };
    }

    if (forecastUnixDate && region && basePeriod && models.length > 0) {
      setIsFetching(true);
      setAreAllImagesFetched(false);

      const newImages: NumericForecastImage[] = [];
      const addImages = async (params) => {
        const res = await trigger(params, true);
        const fetchedImages = res.data;

        if (canceled) return;

        if (fetchedImages) {
          newImages.push(...fetchedImages);
          const newImagesArray = [...newImages];
          setImages(newImagesArray);

          if (fetchedImages.length > maxRowSize) maxRowSize = fetchedImages.length;

          if (multiMapMode && models.length > 1) {
            // Mostra ao menos 3 colunas ao consultar vários modelos (ex.: "Todos"),
            // sem alterar o layout antes da consulta de fato ocorrer.
            const minColumns = 3;
            const upperBound = Math.max(rowSizeRef.current, minColumns);
            const newRowSize = Math.min(maxRowSize, upperBound);
            setMaxRowSize(maxRowSize);
            setRowSize(newRowSize);
          } else {
            const minColumns = 3;
            const upperBound = Math.max(rowSizeRef.current, minColumns);
            const newRowSize = Math.min(newImages.length, upperBound);
            setMaxRowSize(newImages.length);
            setRowSize(newRowSize);
          }
        }
        setIsFetching(false);
      };

      if (multiMapMode) {
        const initial = (basePeriod && basePeriod[0] === 'd') ? 1 : 0;
        const imagePromises: Promise<void>[] = [];
        for (let i = initial; i < maxOffset + 1; i += 1) {
          const period = basePeriod.replace(/\d+/, i.toString(10));
          imagePromises.push(addImages({
            forecastUnixDate, models, region, variable, period, anomaly,
          }));
        }
        Promise.all(imagePromises)
          .catch((failure) => reportImagesFailure(failure, {
            fluxo: 'multi-mapa', models, region, variable, basePeriod, anomaly, maxOffset,
          }))
          .finally(() => {
            setAreAllImagesFetched(true);
          });
      } else {
        const period = basePeriod.replace(/\d+/, offset.toString(10));
        addImages({
          forecastUnixDate, models, region, variable, period, anomaly,
        })
          .catch((failure) => reportImagesFailure(failure, {
            fluxo: 'mapa-unico', models, region, variable, period, anomaly,
          }))
          .finally(() => {
            setAreAllImagesFetched(true);
          });
      }
    } else {
      setImages([]);
      setIsFetching(false);
      setAreAllImagesFetched(false);
    }

    return () => {
      canceled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultRequestId, multiMapMode]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return undefined;
    }
    if (consultRequestId === 0) return undefined;
    if (multiMapMode || viewMode === 'heatmap') return undefined;
    if (!(forecastUnixDate && region && basePeriod && models.length > 0)) return undefined;

    // Refetch automático aqui é só para navegação de dia (slider), quando os
    // demais filtros ainda são os mesmos da última consulta. Se algum filtro
    // foi editado (ex.: trocar o Período) sem nova consulta, não busca.
    const sameAsLastConsulted = lastConsulted
      && lastConsulted.viewMode === viewMode
      && lastConsulted.variable === variable
      && lastConsulted.region === region
      && lastConsulted.period === basePeriod
      && lastConsulted.forecastUnixDate === forecastUnixDate
      && lastConsulted.anomaly === anomaly
      && lastConsulted.models.length === models.length
      && lastConsulted.models.every((model, index) => model === models[index]);
    if (!sameAsLastConsulted) return undefined;

    let canceled = false;
    const period = basePeriod.replace(/\d+/, offset.toString(10));
    setIsFetching(true);
    setAreAllImagesFetched(false);
    trigger({
      forecastUnixDate, models, region, variable, period, anomaly,
    }, true)
      .then((res) => {
        if (canceled) return;
        const fetched = res.data || [];
        setImages(fetched);
        const minColumns = 3;
        const upperBound = Math.max(rowSizeRef.current, minColumns);
        setMaxRowSize(fetched.length);
        setRowSize(Math.min(fetched.length, upperBound));
      })
      .catch((failure) => reportImagesFailure(failure, {
        fluxo: 'navegacao-de-dia', models, region, variable, period, anomaly, offset,
      }))
      .finally(() => {
        if (canceled) return;
        setIsFetching(false);
        setAreAllImagesFetched(true);
      });
    return () => { canceled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  return {
    images,
    isFetching,
    error: viewMode === 'heatmap' ? heatmapError : error,
    areAllImagesFetched,
  };
}
